import React from 'react';
import {
  EyeIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { certificatesApi } from '../../utils/api';
import { queryKeys } from '../../hooks/useData';
import { RowActionsMenu, RowAction } from '../ui/RowActionsMenu';
import { useToast } from '../../contexts/ToastContext';

type Cert = {
  id: string;
  status?: string;
  pdfUrl?: string | null;
  publicToken?: string | null;
};

type Props = {
  certificate: Cert;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  viewLoading?: boolean;
};

export function CertificateActionsMenu({ certificate, onView, onEdit, viewLoading }: Props) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const isDraft = String(certificate.status || '').toUpperCase() === 'DRAFT';

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.certificates });

  const handleRegenerate = async () => {
    try {
      await certificatesApi.generatePdf(certificate.id);
      refresh();
      showToast('Certificate PDF regenerated', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to regenerate', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this certificate? This cannot be undone.')) return;
    try {
      await certificatesApi.delete(certificate.id);
      refresh();
      showToast('Certificate deleted', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const handleCopyLink = async () => {
    if (certificate.pdfUrl) {
      try {
        await navigator.clipboard.writeText(certificate.pdfUrl);
        showToast('PDF link copied', 'success');
      } catch {
        showToast(certificate.pdfUrl, 'info');
      }
    } else {
      showToast('Generate the PDF first to get a shareable link.', 'info');
    }
  };

  const actions: RowAction[] = [
    {
      id: 'view',
      label: viewLoading ? 'Opening…' : 'View certificate',
      icon: EyeIcon,
      disabled: !!viewLoading,
      onClick: () => onView?.(certificate.id),
    },
    {
      id: 'edit',
      label: 'Edit details',
      icon: PencilIcon,
      hidden: !onEdit || !isDraft,
      onClick: () => onEdit?.(certificate.id),
    },
    {
      id: 'regenerate',
      label: 'Regenerate PDF',
      icon: ArrowPathIcon,
      onClick: handleRegenerate,
    },
    {
      id: 'copy-link',
      label: 'Copy PDF link',
      icon: LinkIcon,
      onClick: handleCopyLink,
      disabled: !certificate.pdfUrl,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: TrashIcon,
      danger: true,
      divider: true,
      onClick: handleDelete,
    },
  ];

  return <RowActionsMenu actions={actions} label="Certificate actions" />;
}

export default CertificateActionsMenu;
